import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Handle Stripe checkout for premium subscription
  const handlePremiumCheckout = async () => {
    // Redirect to login if not authenticated
    if (!user) {
      navigate('/login?redirect=/pricing-page');
      return;
    }

    try {
      setIsProcessingPayment(true);
      
      // Create subscription payment intent
      const paymentIntent = await subscriptionService?.createSubscriptionPaymentIntent('business');
      
      if (paymentIntent?.clientSecret) {
        // Redirect to Stripe checkout
        // In production, this should use Stripe Elements or redirect to Stripe hosted checkout
        window.location.href = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/create-subscription-payment?client_secret=${paymentIntent?.clientSecret}`;
      } else {
        throw new Error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Error initiating checkout:', error);
      alert('Unable to process payment. Please try again or contact support.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Handle free tier signup
  const handleFreeSignup = () => {
    if (!user) {
      navigate('/login?redirect=/ipm-operations-overview');
    } else {
      navigate('/ipm-operations-overview');
    }
  };

  const plans = [
    {
      name: 'Personal Grower',
      subtitle: 'Home cultivation',
      price: 'FREE',
      period: 'Always free',
      badge: null,
      features: [
        'AI Plant Diagnosis System',
        'Plant Health Scoring',
        'Weather-Based Growing Tips',
        'Pest ID Database Access',
        'Community Success Stories',
        'Seasonal Growing Calendar'
      ],
      note: 'Perfect for home growers',
      cta: 'Get Started Free',
      popular: false,
      variant: 'outline',
      action: handleFreeSignup
    },
    {
      name: 'Commercial Operation',
      subtitle: 'Professional facilities',
      price: '$14.99',
      period: '/month',
      savings: null,
      trialText: '14-day free trial',
      badge: 'MOST POPULAR',
      features: [
        'Everything in Free Plan',
        'Custom IPM Treatments',
        'Advanced Calculators',
        'Team Management',
        'Advanced Analytics',
        'Priority AI Support'
      ],
      note: 'For agricultural needs',
      cta: 'Start 14-Day Free Trial',
      popular: true,
      variant: 'default',
      action: handlePremiumCheckout
    }
  ];

  const premiumFeatures = [
    {
      icon: 'Settings',
      title: 'Custom IPM Treatments',
      description: 'Your pest list → Your pressure level → Your crop → AI builds a clean, weekly, safe IPM rotation.',
      highlight: true,
      onClick: () => navigate('/premium-calculator-suite')
    },
    {
      icon: 'Calculator',
      title: 'Advanced Calculators',
      description: 'Beneficial and chemical application calculators for precise mixing ratios and optimal release quantities.',
      highlight: true,
      onClick: () => navigate('/preview/calculators')
    },
    {
      icon: 'Building',
      title: 'Team Management',
      description: 'Unlimited rooms & crops. Track multiple grows, greenhouses, or facilities without limits.',
      highlight: true,
      onClick: () => navigate('/preview/team-performance')
    },
    {
      icon: 'BarChart3',
      title: 'Advanced Analytics',
      description: 'Exportable PDF reports perfect for compliance, audits, and commercial recordkeeping.',
      highlight: true,
      onClick: () => navigate('/preview/treatment-analytics')
    },
    {
      icon: 'Camera',
      title: 'Tracking Camera Integration',
      description: 'Advanced camera tracking system for automated pest monitoring and population analysis.',
      highlight: true,
      onClick: () => navigate('/preview/treatment-analytics')
    },
    {
      icon: 'HeadphonesIcon',
      title: 'Priority AI Support',
      description: 'Faster response times and priority access to our AI-powered agricultural support system.',
      highlight: true,
      onClick: () => navigate('/compliance-dashboard')
    }
  ];

  const comparisonFeatures = [
    { feature: 'AI Plant Diagnosis System', free: true, pro: true },
    { feature: 'Plant Health Scoring', free: true, pro: true },
    { feature: 'Weather-Based Growing Tips', free: true, pro: true },
    { feature: 'Pest ID Database Access', free: true, pro: true },
    { feature: 'Community Success Stories', free: true, pro: true },
    { feature: 'Seasonal Growing Calendar', free: true, pro: true },
    { feature: 'Custom IPM Treatments', free: false, pro: true },
    { feature: 'Advanced Calculators', free: false, pro: true },
    { feature: 'Team Management', free: false, pro: true },
    { feature: 'Advanced Analytics', free: false, pro: true },
    { feature: 'Priority AI Support', free: false, pro: true },
    { feature: 'Tracking Camera Integration', free: false, pro: true }
  ];

  const faqs = [
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes, cancellations take effect immediately. No questions asked.'
    },
    {
      question: 'Is Grow Guardian only for cannabis?',
      answer: 'No — it works for any crop including vegetables, fruits, cannabis, ornamentals, and greenhouse products.'
    },
    {
      question: 'Can I use it in greenhouses?',
      answer: 'Yes, indoor, outdoor, and greenhouse growers use it successfully for all types of crops.'
    },
    {
      question: 'What is the Custom IPM Treatment Generator?',
      answer: 'Our AI builds a personalized weekly spray plan based on your pressure ratings, pest list, crop type, and growing conditions.'
    },
    {
      question: 'Will beneficial insects be harmed by the recommendations?',
      answer: 'The AI system specifically avoids harmful interactions with beneficial insects and recommends beneficial-friendly products.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes, we use encrypted cloud storage and never sell your agricultural data.'
    },
    {
      question: 'Do the calculators work for all crop types?',
      answer: 'Yes, our advanced calculators are designed to work with vegetables, fruits, cannabis, and other agricultural crops.'
    },
    {
      question: 'What is tracking camera integration?',
      answer: 'Our premium plan includes integration with camera systems for automated pest monitoring and population tracking across your growing areas.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Vegetable Grower',
      content: 'Reduced pest outbreaks by 80% in my greenhouse operation and saved $15K annually on treatments.',
      savings: '$15,000 saved annually'
    },
    {
      name: 'Mike R.',
      role: 'Cannabis Cultivator',
      content: 'The custom IPM generator and calculators alone pay for themselves. Complete game changer.',
      savings: '3x ROI in first month'
    },
    {
      name: 'Lisa K.',
      role: 'Fruit Farm Manager',
      content: 'Perfect for compliance tracking. State inspections are now stress-free with the analytics.',
      savings: '50% time savings'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/images/GrowGuardianGGlogo-1763327971762.png" 
                alt="GrowGuardian logo"
                className="w-10 h-10 object-contain"
              />
              <div className="flex flex-col">
                <h1 className="text-lg font-bold text-gray-900">GrowGuardian</h1>
                <span className="text-xs text-gray-500">AI Agricultural Companion</span>
              </div>
            </button>

            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900">Home</button>
              <button onClick={() => navigate('/#features')} className="text-gray-600 hover:text-gray-900">Features</button>
              <span className="text-green-600 font-medium">Pricing</span>
              <button onClick={() => navigate('/#about')} className="text-gray-600 hover:text-gray-900">About</button>
            </nav>

            <Button
              onClick={() => navigate('/ipm-operations-overview')}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              View Analytics
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/assets/images/GrowGuardianGGlogo-1763327971762.png" 
              alt="GrowGuardian logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Grow Guardian Pricing
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            AI-powered agricultural companion for vegetables, fruits, and cannabis crops. 
            Start free on mobile or upgrade for professional features.
          </p>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans?.map((plan, index) => (
              <div 
                key={index} 
                className={`rounded-lg p-8 border-2 transition-all ${
                  plan?.popular 
                    ? 'border-green-600 bg-green-50 scale-105 shadow-lg' 
                    : 'border-gray-200 bg-white hover:border-green-300'
                } relative`}
              >
                {plan?.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {plan?.badge}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan?.name}</h3>
                  <p className="text-gray-600 mb-4">{plan?.subtitle}</p>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-gray-900">{plan?.price}</span>
                    <span className="text-gray-600">{plan?.period}</span>
                  </div>
                  {plan?.trialText && (
                    <p className="text-green-600 text-sm mt-2">{plan?.trialText}</p>
                  )}
                  {plan?.note && (
                    <p className="text-gray-500 text-sm mt-2">{plan?.note}</p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan?.features?.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center space-x-3">
                      <Icon name="Check" size={16} className="text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan?.variant}
                  onClick={plan?.action}
                  disabled={isProcessingPayment && plan?.popular}
                  className={`w-full ${
                    plan?.popular 
                      ? 'bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400' 
                      : plan?.variant === 'outline' ? 'border-green-600 text-green-600 hover:bg-green-50' : ''
                  }`}
                >
                  {isProcessingPayment && plan?.popular ? 'Processing...' : plan?.cta}
                </Button>

                {/* Stripe Badge for Commercial Plan */}
                {plan?.popular && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                    <Icon name="Lock" size={12} />
                    <span>Secure payment powered by Stripe</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Features Highlight */}
      <section className="py-16 bg-gradient-to-b from-green-900 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advanced Tools for Professional Agriculture</h2>
            <p className="text-green-100 text-lg">14-day free trial • No setup fees • Cancel anytime</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {premiumFeatures?.map((feature, index) => (
              <button
                key={index}
                onClick={feature?.onClick}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 hover:bg-white/15 transition-colors text-left w-full"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center">
                    <Icon name={feature?.icon} size={24} className="text-green-900" />
                  </div>
                  <h3 className="text-xl font-semibold">{feature?.title}</h3>
                </div>
                <p className="text-green-100 leading-relaxed">{feature?.description}</p>
              </button>
            ))}
          </div>

          <div className="text-center mt-12">
            <h3 className="text-2xl font-bold mb-4">Why Grow Guardian?</h3>
            <p className="text-green-100 text-lg leading-relaxed max-w-4xl mx-auto mb-6">
              Built by growers for growers. Our AI-powered system combines real agricultural experience with 
              cutting-edge technology to help prevent outbreaks, protect beneficial insects, and keep your crops healthy.
            </p>
            <p className="text-yellow-400 text-xl font-semibold mb-6">
              Grow smart. Grow safe. Grow Guardian.
            </p>
            <Button
              onClick={handlePremiumCheckout}
              disabled={isProcessingPayment}
              className="bg-white text-green-800 hover:bg-gray-100 px-8 py-3 disabled:bg-gray-300"
            >
              {isProcessingPayment ? 'Processing...' : 'Try Premium Features'}
            </Button>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Feature Comparison</h2>
            <p className="text-lg text-gray-600">See exactly what's included in each plan</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Features</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">Personal Grower</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-900 bg-green-50">Commercial Operation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparisonFeatures?.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{item?.feature}</td>
                      <td className="px-6 py-4 text-center">
                        {item?.free === true ? (
                          <Icon name="Check" size={16} className="text-green-600 mx-auto" />
                        ) : item?.free === false ? (
                          <Icon name="X" size={16} className="text-gray-400 mx-auto" />
                        ) : (
                          <span className="text-xs text-gray-600">{item?.free}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center bg-green-50">
                        {item?.pro === true ? (
                          <Icon name="Check" size={16} className="text-green-600 mx-auto" />
                        ) : (
                          <Icon name="X" size={16} className="text-gray-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-lg text-gray-600">Real results from growers using Grow Guardian</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials?.map((testimonial, index) => (
              <div key={index} className="bg-green-50 rounded-lg p-6 border border-green-200">
                <div className="mb-4">
                  <div className="flex text-yellow-400 mb-2">
                    {[...Array(5)]?.map((_, i) => (
                      <Icon key={i} name="Star" size={16} className="fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic">"{testimonial?.content}"</p>
                </div>
                <div className="border-t border-green-200 pt-4">
                  <p className="font-semibold text-gray-900">{testimonial?.name}</p>
                  <p className="text-sm text-gray-600">{testimonial?.role}</p>
                  <p className="text-sm text-green-600 font-medium mt-1">{testimonial?.savings}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Signals */}
          <div className="mt-12 text-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center justify-center space-x-3">
                <Icon name="Shield" size={24} className="text-green-600" />
                <span className="text-gray-700">14-day money-back guarantee</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Icon name="CreditCard" size={24} className="text-green-600" />
                <span className="text-gray-700">No setup fees or contracts</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Icon name="Lock" size={24} className="text-green-600" />
                <span className="text-gray-700">Secure agricultural data</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about Grow Guardian pricing</p>
          </div>

          <div className="space-y-4">
            {faqs?.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg bg-white">
                <button
                  onClick={() => setSelectedFAQ(selectedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{faq?.question}</span>
                  <Icon 
                    name={selectedFAQ === index ? 'ChevronUp' : 'ChevronDown'} 
                    size={20} 
                    className="text-gray-500" 
                  />
                </button>
                {selectedFAQ === index && (
                  <div className="px-6 pb-4 border-t border-gray-200 pt-4">
                    <p className="text-gray-600 leading-relaxed">{faq?.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Stripe Integration */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Growing Operation?</h2>
          <p className="text-xl text-green-100 mb-8">
            Join thousands of growers who trust Grow Guardian's AI-powered agricultural companion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handlePremiumCheckout}
              disabled={isProcessingPayment}
              className="bg-white text-green-600 hover:bg-gray-50 px-8 py-4 text-lg disabled:bg-gray-300"
            >
              {isProcessingPayment ? 'Processing...' : 'Start 14-Day Free Trial'}
            </Button>
            <Button 
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              onClick={() => navigate('/')}
            >
              Learn More
            </Button>
          </div>
          
          {/* Stripe Trust Badge */}
          <div className="mt-8 flex items-center justify-center gap-4 text-sm opacity-80">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={16} />
              <span>Secure payments by Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={16} />
              <span>SSL encrypted</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <button 
                onClick={() => navigate('/')}
                className="flex items-center space-x-3 mb-4 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/assets/images/GrowGuardianGGlogo-1763327971762.png" 
                  alt="GrowGuardian logo"
                  className="w-8 h-8 object-contain"
                />
                <h3 className="text-lg font-bold">GrowGuardian</h3>
              </button>
              <p className="text-gray-400 mb-4">
                AI-powered agricultural companion for vegetables, fruits, and cannabis crops
              </p>
              <p className="text-gray-400 text-sm">
                Grow Guardian LLC – Colorado
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/#features')} className="text-gray-400 hover:text-white transition-colors text-left">Features</button></li>
                <li><button onClick={() => navigate('/#premium')} className="text-gray-400 hover:text-white transition-colors text-left">Premium</button></li>
                <li><span className="text-white">Pricing</span></li>
                <li><button onClick={() => navigate('/application-logs')} className="text-gray-400 hover:text-white transition-colors text-left">Application Logs</button></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="mailto:support@growguardian.work" className="text-gray-400 hover:text-white transition-colors">support@growguardian.work</a></li>
                <li><button onClick={() => navigate('#faq')} className="text-gray-400 hover:text-white transition-colors text-left">FAQ</button></li>
                <li><button onClick={() => navigate('/about-us-page')} className="text-gray-400 hover:text-white transition-colors text-left">About Us</button></li>
                <li><button onClick={() => navigate('/product-browser')} className="text-gray-400 hover:text-white transition-colors text-left">Product Browser</button></li>
              </ul>
            </div>

            {/* Analytics */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Analytics Dashboard</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/ipm-operations-overview')} className="text-gray-400 hover:text-white transition-colors text-left">Operations Overview</button></li>
                <li><button onClick={() => navigate('/treatment-analytics')} className="text-gray-400 hover:text-white transition-colors text-left">Treatment Analytics</button></li>
                <li><button onClick={() => navigate('/team-performance-hub')} className="text-gray-400 hover:text-white transition-colors text-left">Team Performance</button></li>
                <li><button onClick={() => navigate('/compliance-dashboard')} className="text-gray-400 hover:text-white transition-colors text-left">Compliance Dashboard</button></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                <span>Website: </span>
                <a href="https://growguardian.work" className="text-green-400 hover:text-green-300 transition-colors" target="_blank" rel="noopener noreferrer">
                  GrowGuardian.work
                </a>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <button onClick={() => navigate('#terms')} className="text-gray-400 hover:text-white transition-colors">Terms & Privacy</button>
                <button onClick={() => navigate('#faq')} className="text-gray-400 hover:text-white transition-colors">FAQ</button>
                <a href="mailto:support@growguardian.work" className="text-gray-400 hover:text-white transition-colors">App Support</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;